import React, { useRef, useEffect, useState, useMemo } from "react";

// --- Treemap Logic (Ported from Squarify Algorithm) ---

function worstAspect(row, side, totalArea) {
  if (row.length === 0) return Infinity;
  const rowTotal = row.reduce((s, it) => s + it.value, 0);
  if (rowTotal === 0 || totalArea === 0) return Infinity;
  const rowWidth = rowTotal / side;
  let worst = 0;
  row.forEach(item => {
    const h = item.value / rowWidth;
    const aspect = Math.max(rowWidth / h, h / rowWidth);
    if (aspect > worst) worst = aspect;
  });
  return worst;
}

function squarify(items, x, y, width, height) {
  let cx = x, cy = y, cw = width, ch = height;
  let remaining = [...items];
  const totalArea = items.reduce((s, it) => s + it.value, 0);
  
  const rects = [];
  while (remaining.length > 0) {
    let row = [remaining[0]];
    let side = Math.min(cw, ch);
    let i = 1;
    while (i < remaining.length) {
      if (worstAspect([...row, remaining[i]], side, totalArea) <= worstAspect(row, side, totalArea)) {
        row.push(remaining[i]);
        i++;
      } else {
        break;
      }
    }
    
    // Layout the row
    const rowArea = row.reduce((s, it) => s + it.value, 0);
    const rowSize = rowArea / side;
    const isHorizontal = cw >= ch;
    
    let offset = 0;
    row.forEach(item => {
      const itemSize = item.value / rowSize;
      if (isHorizontal) {
        rects.push({ ...item, rx: cx, ry: cy + offset, rw: rowSize, rh: itemSize });
      } else {
        rects.push({ ...item, rx: cx + offset, ry: cy, rw: itemSize, rh: rowSize });
      }
      offset += itemSize;
    });
    
    // Shrink remaining area
    if (isHorizontal) {
      cx += rowSize;
      cw -= rowSize;
    } else {
      cy += rowSize;
      ch -= rowSize;
    }
    remaining = remaining.slice(row.length);
  }
  return rects;
}

const perfColor = (pct) => {
  if (pct == null) return '#18181b';
  const clamped = Math.max(-5, Math.min(5, pct));
  const t = (clamped + 5) / 10;
  
  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const rgb = (r, g, b) => `rgb(${r},${g},${b})`;

  if (t < 0.35) {
    const s = t / 0.35;
    return rgb(lerp(180, 120, s), lerp(30, 60, s), lerp(30, 30, s));
  } else if (t < 0.5) {
    const s = (t - 0.35) / 0.15;
    return rgb(lerp(120, 60, s), lerp(60, 60, s), lerp(30, 55, s));
  } else if (t < 0.65) {
    const s = (t - 0.5) / 0.15;
    return rgb(lerp(60, 30, s), lerp(60, 100, s), lerp(55, 50, s));
  } else {
    const s = (t - 0.65) / 0.35;
    return rgb(lerp(30, 0, s), lerp(100, 160, s), lerp(50, 70, s));
  }
};

const ETFHeatmap = ({ data, sizeBy, currentSet, onTileClick }) => {
  const containerRef = useRef(null);
  const [rects, setRects] = useState([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const sizeField = sizeBy === 'month' ? 'return_6m' : sizeBy === 'week' ? 'return_1m' : 'return_3m';

  useEffect(() => {
    if (dimensions.width === 0 || data.length === 0) return;

    const MIN_VAL = 0.8;
    const GAP = 4;
    const HEADER_H = 20;

    const groups = [...new Set(data.map(d => d.region))];
    const groupedData = groups.map(g => {
        const items = data.filter(d => d.region === g);
        const raw = items.reduce((s, etf) => s + Math.max(MIN_VAL, Math.abs(etf[sizeField] || 0)), 0);
        return { group: g, value: Math.max(2.5, raw), etfs: items };
    });

    const totalVal = groupedData.reduce((s, g) => s + g.value, 0);
    const scale = (dimensions.width * dimensions.height) / totalVal;
    
    const groupItems = groupedData.map(g => ({ ...g, value: g.value * scale }));
    const calculatedGroupRects = squarify(groupItems, 0, 0, dimensions.width, dimensions.height);

    const finalRects = [];
    calculatedGroupRects.forEach(gr => {
      const gX = gr.rx + GAP / 2;
      const gY = gr.ry + GAP / 2;
      const gW = gr.rw - GAP;
      const gH = gr.rh - GAP;
      if (gW <= 0 || gH <= 0) return;

      const innerW = gW;
      const innerH = gH - HEADER_H;
      if (innerH <= 0) return;

      const innerItems = gr.etfs.map(etf => ({
        etf,
        value: Math.max(MIN_VAL, Math.abs(etf[sizeField] || 0))
      }));
      
      const groupInnerTotal = innerItems.reduce((s, i) => s + i.value, 0);
      const innerScale = (innerW * innerH) / groupInnerTotal;
      const scaledInnerItems = innerItems.map(i => ({ ...i, value: i.value * innerScale })).sort((a,b) => b.value - a.value);

      const cellRects = squarify(scaledInnerItems, 0, 0, innerW, innerH);
      
      finalRects.push({
        group: gr.group,
        x: gX,
        y: gY,
        w: gW,
        h: gH,
        cells: cellRects.map(cr => ({
          ...cr,
          etf: cr.etf,
          color: perfColor(cr.etf[sizeField])
        }))
      });
    });

    setRects(finalRects);
  }, [data, sizeField, dimensions]);

  return (
    <div className="relative w-full aspect-[2.2/1] min-h-[400px] bg-black group" ref={containerRef}>
      {rects.map((gr, i) => (
        <div 
          key={i}
          className="absolute overflow-hidden rounded-md border border-white/5"
          style={{ 
            left: gr.x, 
            top: gr.y, 
            width: gr.w, 
            height: gr.h,
            backgroundColor: 'rgba(24, 24, 27, 0.4)'
          }}
        >
          <div className="bg-zinc-900/80 px-2 py-0.5 text-[12px] font-black uppercase text-zinc-500 border-b border-white/5 truncate">
            {gr.group}
          </div>
          <div className="relative w-full h-full">
            {gr.cells.map((cr, j) => (
              <div
                key={j}
                className="absolute flex flex-col items-center justify-center p-1 cursor-pointer transition-all hover:brightness-125 hover:scale-[1.02] hover:z-10 border border-black/20"
                style={{
                  left: cr.rx,
                  top: cr.ry,
                  width: cr.rw,
                  height: cr.rh,
                  backgroundColor: cr.color
                }}
                onClick={() => onTileClick(cr.etf.ticker)}
                onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltip({
                        etf: cr.etf,
                        x: rect.left + rect.width / 2,
                        y: rect.top - 10
                    });
                }}
                onMouseLeave={() => setTooltip(null)}
              >
                <span className="font-black text-white drop-shadow-md leading-none select-none" style={{ fontSize: Math.max(8, Math.min(16, Math.min(cr.rw, cr.rh) * 0.25)) }}>
                  {cr.etf.ticker}
                </span>
                <span className="font-bold text-white/90 drop-shadow-sm select-none" style={{ fontSize: Math.max(7, Math.min(12, Math.min(cr.rw, cr.rh) * 0.18)) }}>
                  {(cr.etf[sizeField] || 0) >= 0 ? '+' : ''}{(cr.etf[sizeField] || 0).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {tooltip && (
        <div 
          className="fixed z-[100] pointer-events-none bg-zinc-900 border border-zinc-700 px-3 py-2 rounded-lg shadow-2xl text-[13px] animate-in zoom-in-95 duration-100"
          style={{ 
            left: tooltip.x, 
            top: tooltip.y, 
            transform: 'translate(-50%, -100%)' 
          }}
        >
          <div className="flex justify-between gap-4">
            <span className="text-zinc-500 font-bold uppercase tracking-tighter">Ticker</span>
            <span className="text-white font-bold">{tooltip.etf.ticker}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-zinc-500 font-bold uppercase tracking-tighter">Country</span>
            <span className="text-white">{tooltip.etf.country}</span>
          </div>
          <div className="flex justify-between gap-4 mt-1">
            <span className="text-zinc-500 font-bold uppercase tracking-tighter">Day %</span>
            <span className={`font-bold ${(tooltip.etf.return_1m || 0) >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
              {(tooltip.etf.return_1m || 0).toFixed(2)}%
            </span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-zinc-500 font-bold uppercase tracking-tighter">1Y Return</span>
            <span className={`font-bold ${(tooltip.etf.return_1y || 0) >= 0 ? "text-emerald-500" : "text-rose-500"}`}>
              {(tooltip.etf.return_1y || 0).toFixed(2)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ETFHeatmap;
